export type QueryParams = Record<string, string | string[] | null>

// Per application/x-www-form-urlencoded, '+' means space (URLSearchParams and
// the legacy query-string lib both write spaces as '+'); a literal '+' arrives
// encoded as '%2B', so it survives the replace.
const decode = (str: string) => decodeURIComponent(str.replace(/\+/g, ' '))

export const parseQueryString = (search: string): QueryParams => {
  const query = search.startsWith('?') ? search.slice(1) : search
  const result: QueryParams = {}

  if (!query) return result

  for (const part of query.split('&')) {
    if (!part) continue

    const eqIndex = part.indexOf('=')
    const key = decode(eqIndex === -1 ? part : part.slice(0, eqIndex))
    const value = eqIndex === -1 ? null : decode(part.slice(eqIndex + 1))

    const existing = result[key]
    if (existing === undefined) {
      result[key] = value
    } else if (Array.isArray(existing)) {
      existing.push(value as string)
    } else {
      result[key] = [existing as string, value as string]
    }
  }

  return result
}

// Keys are sorted so generated URLs are deterministic regardless of the order
// params were set in; a null value produces a bare key without '='.
export const toQueryString = (params: Record<string, unknown>): string => {
  const encodePair = (key: string, value: unknown) =>
    value === null ? encodeURIComponent(key) : `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`

  return Object.keys(params)
    .sort()
    .filter(key => params[key] !== undefined)
    .flatMap(key => {
      const value = params[key]
      return Array.isArray(value) ? value.map(v => encodePair(key, v)) : [encodePair(key, value)]
    })
    .join('&')
}
